<script lang="ts">
  import { onDestroy, onMount } from "svelte";

  import * as yup from "yup";
  import { Form } from "$ui/form";
  import { Button } from "$ui/button";
  import { Input } from "$ui/input";

  import BinIcon from "$assets/icons/bin.svelte";

  import { cn } from "$utils/ui";
  import { CircleHelp, Plus } from "lucide-svelte";
  import { DrawerHeader, DrawerTitle } from "$lib/ui/drawer";
  import { makeApiRequest } from "$lib/utils/api";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import * as Tooltip from "$ui/tooltip";
  type FormProps = {
    providerName?: string;
    providerId?: string;
    initialValues?: { sites: string[] } | undefined;
    isEdit?: boolean;
    onSuccess?: () => void;
    form?: any;
    errors?: any;
    touched?: any;
    isSubmitting?: any;
    onSubmit?: ({ sites }: { sites: string[] }) => Promise<void>;
    handleSubmit?: () => void;
    standaloneForm?: boolean;
    validateField?: (field?: any) => any;
    updateValidateField?: () => void;
  };

  let isEdit: boolean = false;
  let providerName;
  let providerId;

  let initialValues: any = {
    // Note: initalValues is read once, if the page loads straight with this sidebar open it will not update the sites on the list.
    sites: $selectedOrgStore?.dataSources.find((ds) => ds.provider === providerId)?.scopes ?? [null],
  };

  const validationSchema = yup.object().shape({
    sites: yup.array().of(
      yup
        .string()
        .required("Sites cannot be empty")
        .test("duplicated site", "You already input this site", (value) => {
          return $form.sites.reduce((count: number, o: string) => count + (o === value ? 1 : 0), 0) <= 1;
        }),
    ),
  });

  let standaloneForm: FormProps["standaloneForm"] = true;
  let onSuccess: FormProps["onSuccess"] = () => {};

  let form: FormProps["form"] = null;
  let errors: FormProps["errors"] = null;
  let touched: FormProps["touched"] = null;
  let isSubmitting: FormProps["isSubmitting"] = false;
  let handleSubmit: FormProps["handleSubmit"] = () => {};
  let onSubmit: FormProps["onSubmit"] = async ({ sites }) => {
    const dataSource = $selectedOrgStore?.dataSources.find((ds) => ds.provider === providerId);

    if (!dataSource) {
      throw new Error("Data source not found");
    }

    await makeApiRequest(`${$selectedOrgStore?.resourcePrefix}/`, {
      data_sources: [
        {
          ...dataSource,
          admins: Object.fromEntries(dataSource.invitedAdmins.map((email) => [email, false])),
          scopes: sites,
        },
      ],
    });
  };
  let validateField: FormProps["validateField"];
  let updateValidateField: FormProps["updateValidateField"];

  onMount(() => {
    $touched.sites[0] = null;
  });

  const unsub = selectedOrgStore.subscribe((value) => {
    if (!form) return;
    $form.sites = value?.dataSources.find((ds) => ds.provider === providerId)?.scopes ?? [null];
  });

  onDestroy(unsub);

  const add = () => {
    $form.sites = [...$form.sites, null];
    $errors.sites = [...$errors.sites, ""];
    $touched.sites = [...$touched.sites, null];
  };

  const remove = (index: string | number) => () => {
    $form.sites = $form.sites.filter((_: any, currentIndex: string | number) => currentIndex !== index);
    $errors.sites = $errors.sites.filter((_: any, currentIndex: string | number) => currentIndex !== index);
  };

  export {
    providerName,
    providerId,
    wizardValues,
    form,
    errors,
    touched,
    isSubmitting,
    handleSubmit,
    standaloneForm,
    onSubmit,
    onSuccess,
    initialValues,
    isEdit,
  };
</script>

<DrawerHeader class="pt-8 px-6 pb-4 relative">
  <DrawerTitle class="text-left">Configure your connection to {providerName}</DrawerTitle>
</DrawerHeader>

<div class="flex-1 flex flex-col items-stretch px-6">
  <Form
    {initialValues}
    {validationSchema}
    {onSubmit}
    {onSuccess}
    {standaloneForm}
    submittingButtonText="Saving..."
    submitButtonText="Save Changes"
    bind:form
    bind:handleSubmit
    bind:errors
    bind:validateField
    bind:updateValidateField
    bind:isSubmitting
    bind:touched
  >
    {#if $form}
      <div class="flex items-center gap-2">
        <span>Specify {providerName} sites to index</span>
        <Tooltip.Root>
          <Tooltip.Trigger>
            <CircleHelp size="20" />
          </Tooltip.Trigger>
          <Tooltip.Content class="flex flex-col gap-2 py-3">
            <div class="font-semibold">Limit Sites</div>
            <div>
              Enter the sites you want Permissions Assurance to index. You can add multiple sites by clicking the "Add another site"
              button.
            </div>
          </Tooltip.Content>
        </Tooltip.Root>
      </div>
      {#each $form.sites as site, index}
        <div class="flex flex-col">
          <div class={cn("flex flex-col md:flex-row gap-4 [&>div]:flex-1", isEdit && "flex-col")}>
            <Input
              name={`sites[${index}]`}
              placeholder="site.sharepoint.com"
              error={($errors.sites && $errors.sites[index]) || ""}
              readOnly={isEdit}
              bind:value={site}
              on:input={() => {
                validateField && validateField(`sites[${index}]`);
              }}
            />

            {#if !isEdit}
              <div class="!flex-[0_0_24px] mt-2">
                <button
                  type="button"
                  on:click={remove(index)}
                  disabled={$form.sites.length === 1}
                  class={cn("[&>svg]:w-3.5", $form.sites.length === 1 && "opacity-30 cursor-not-allowed")}
                >
                  <BinIcon />
                </button>
              </div>
            {/if}
          </div>
          {#if index === $form.sites.length - 1 && !isEdit}
            <div class="mt-2">
              <Button on:click={add} variant="outline" size="xs">
                <Plus size={16} />
                Add another site
              </Button>
            </div>
          {/if}
        </div>
      {/each}
    {/if}
  </Form>
</div>
